import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { captureEvent } from '../analytics';

type ScanStatus = 'idle' | 'scanning' | 'complete' | 'error';
type SwapStatus = 'idle' | 'swapping' | 'complete' | 'error';
type MigrationStatus = 'idle' | 'migrating' | 'complete' | 'error';

// Mirror of RetrievalResultsMigrationSummary (services/code_migrations).
interface MigrationSummary {
    total_legacy: number;
    migrated: number;
    skipped_zero_result: number;
    failed: number;
    dropped_ids: number;
}

interface ColumnEntry {
    num_values: number;
    num_non_null: number;
    disabled: boolean;
}

interface ScanSummary {
    totalColumns: number;
    totalTables: number;
    columnsWithData: number;
}

function summarize(map: Record<string, ColumnEntry>): ScanSummary {
    const tables = new Set<string>();
    let columnsWithData = 0;
    for (const [qualname, entry] of Object.entries(map)) {
        const table = qualname.split('.')[0];
        tables.add(table);
        if (entry.num_non_null > 0) columnsWithData++;
    }
    return {
        totalColumns: Object.keys(map).length,
        totalTables: tables.size,
        columnsWithData,
    };
}

export const useDataLoadStore = defineStore('dataLoad', () => {
    const isOpen = ref(false);
    const matterId = ref<string | null>(null);
    const scanStatus = ref<ScanStatus>('idle');
    const scanSummary = ref<ScanSummary | null>(null);
    const errorMessage = ref<string | null>(null);

    // Case-data swap (replace the matter's cdm data from a server backup).
    const availableBackups = ref<string[]>([]);
    const selectedBackup = ref<string | null>(null);
    const swapStatus = ref<SwapStatus>('idle');
    const swapErrorMessage = ref<string | null>(null);

    // One-off legacy-results code migration (retrieval_results -> subsets).
    const migrationStatus = ref<MigrationStatus>('idle');
    const migrationSummary = ref<MigrationSummary | null>(null);
    const migrationErrorMessage = ref<string | null>(null);

    // null = status not yet fetched (fail-open); true/false = definitive result.
    // Mirrors the matterContextStatus pattern in chatStore.
    const hasMap = ref<boolean | null>(null);

    const isScanning = computed(() => scanStatus.value === 'scanning');
    const isSwapping = computed(() => swapStatus.value === 'swapping');
    const isMigrating = computed(() => migrationStatus.value === 'migrating');

    // True only when we have a definitive answer that no scan has been run.
    // While hasMap is null (unfetched or transient error) this stays false so
    // a network hiccup never locks the user out.
    const scanRequired = computed(() => hasMap.value === false);

    function open(id: string) {
        matterId.value = id;
        isOpen.value = true;
        // Populate the swap picker in the background; failures are swallowed
        // (the section just shows no backups available).
        void fetchBackups();
    }

    function close() {
        isOpen.value = false;
        // Reset the transient scan UI so the dialog opens fresh next time
        // rather than showing the previous run's result/error. hasMap (the
        // persisted "has a scan run" status driving the lock overlay) and
        // matterId (reset by open()) are intentionally left untouched.
        scanStatus.value = 'idle';
        scanSummary.value = null;
        errorMessage.value = null;
        // Reset the swap UI too. availableBackups is left cached — the list is
        // matter-independent and cheap to keep; selectedBackup is reset so the
        // dialog never reopens with a stale, possibly-deleted selection.
        swapStatus.value = 'idle';
        swapErrorMessage.value = null;
        selectedBackup.value = null;
        // And the migration UI, same transient-reset rationale as the scan.
        migrationStatus.value = 'idle';
        migrationSummary.value = null;
        migrationErrorMessage.value = null;
    }

    async function fetchMapStatus(id: string): Promise<boolean | null> {
        try {
            const response = await fetch(`/api/Matter/${id}/AvailabilityMap`);
            if (response.ok) {
                const data = await response.json();
                const result = data.availability_map !== null;
                hasMap.value = result;
                return result;
            }
        } catch (err) {
            console.warn('Failed to fetch availability map status:', err);
        }
        return null;
    }

    async function runScan() {
        if (!matterId.value || isScanning.value) return;
        // Captured up front so both the success and error paths below can
        // reference it (the catch block doesn't get the ref narrowing).
        const scanMatterId = matterId.value;

        scanStatus.value = 'scanning';
        errorMessage.value = null;
        scanSummary.value = null;

        try {
            const response = await fetch(
                `/api/Matter/${scanMatterId}/AvailabilityMap/Scan`,
                { method: 'POST' },
            );

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.detail ?? `Scan failed (${response.status})`);
            }

            const data = await response.json();
            const summary = summarize(data.availability_map ?? {});
            scanSummary.value = summary;
            scanStatus.value = 'complete';
            hasMap.value = true;

            // Onboarding-funnel step (matter context → data load → first
            // question). Counts only — the summary is already PII-free
            // (table/column counts, never names or values).
            captureEvent('data_load_scan_completed', {
                matter_id: scanMatterId,
                status: 'complete',
                total_tables: summary.totalTables,
                total_columns: summary.totalColumns,
                columns_with_data: summary.columnsWithData,
            });
        } catch (e: unknown) {
            errorMessage.value = e instanceof Error ? e.message : 'Unknown error';
            scanStatus.value = 'error';
            // A failed scan is a real onboarding drop-off — emit the same
            // event with status 'error' so it's a filterable property on the
            // funnel rather than an invisible gap. No error text (may carry
            // backend detail) — just the status flag.
            captureEvent('data_load_scan_completed', {
                matter_id: scanMatterId,
                status: 'error',
            });
        }
    }

    async function fetchBackups(): Promise<void> {
        try {
            const response = await fetch(`/api/Backups`);
            if (response.ok) {
                const data = await response.json();
                availableBackups.value = data.backups ?? [];
            }
        } catch (err) {
            console.warn('Failed to fetch available backups:', err);
        }
    }

    async function swapCaseData() {
        if (!matterId.value || !selectedBackup.value || isSwapping.value) return;
        // Captured up front so the catch block (which doesn't get the ref
        // narrowing) can still reference them.
        const swapMatterId = matterId.value;
        const backupName = selectedBackup.value;

        swapStatus.value = 'swapping';
        swapErrorMessage.value = null;

        try {
            const response = await fetch(
                `/api/Matter/${swapMatterId}/SwapCaseData`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ backupName }),
                },
            );

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.detail ?? `Swap failed (${response.status})`);
            }

            // The swap endpoint re-scans and returns the fresh availability map,
            // so the scan summary updates in place — no separate scan click.
            const data = await response.json();
            const summary = summarize(data.availability_map ?? {});
            scanSummary.value = summary;
            scanStatus.value = 'complete';
            hasMap.value = true;
            swapStatus.value = 'complete';

            // The backup filename is intentionally NOT in the payload — it can
            // carry client/matter identifiers. Counts only, like the scan event.
            captureEvent('data_load_swap_completed', {
                matter_id: swapMatterId,
                status: 'complete',
                total_tables: summary.totalTables,
                total_columns: summary.totalColumns,
                columns_with_data: summary.columnsWithData,
            });
        } catch (e: unknown) {
            swapErrorMessage.value = e instanceof Error ? e.message : 'Unknown error';
            swapStatus.value = 'error';
            captureEvent('data_load_swap_completed', {
                matter_id: swapMatterId,
                status: 'error',
            });
        }
    }

    async function runLegacyResultsMigration() {
        if (!matterId.value || isMigrating.value) return;

        migrationStatus.value = 'migrating';
        migrationErrorMessage.value = null;
        migrationSummary.value = null;

        try {
            const response = await fetch(
                `/api/Matter/${matterId.value}/CodeMigrations/RetrievalResults`,
                { method: 'POST' },
            );

            if (!response.ok) {
                const body = await response.json().catch(() => ({}));
                throw new Error(body?.detail ?? `Migration failed (${response.status})`);
            }

            migrationSummary.value = (await response.json()) as MigrationSummary;
            migrationStatus.value = 'complete';
        } catch (e: unknown) {
            migrationErrorMessage.value = e instanceof Error ? e.message : 'Unknown error';
            migrationStatus.value = 'error';
        }
    }

    return {
        isOpen,
        matterId,
        scanStatus,
        scanSummary,
        errorMessage,
        hasMap,
        isScanning,
        scanRequired,
        availableBackups,
        selectedBackup,
        swapStatus,
        swapErrorMessage,
        isSwapping,
        migrationStatus,
        migrationSummary,
        migrationErrorMessage,
        isMigrating,
        open,
        close,
        fetchMapStatus,
        fetchBackups,
        runScan,
        swapCaseData,
        runLegacyResultsMigration,
    };
});
